import { useQueryClient } from '@tanstack/react-query';
import { router, useLocalSearchParams } from 'expo-router';
import { useSetAtom } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BackButton,
    BackButtonIconType,
    DotNavigation,
    FullScreenSpinner,
    isNonMobileWeb,
    ScreenContainer,
    TallSheet,
    useToastController,
    View,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { captureException } from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import useRunKYCVerify, {
    type RunKYCVerifyBody,
} from '../../hooks/kyc/useRunKycVerify';
import { addressAtom, type PrepopulatedAddress } from '../../state/SpacesAtoms';
import { KYCVerificationStatus } from '../../types/BankAccount';
import {
    FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES,
    getBankingDisclosures,
} from '../Disclosures';
import Address from './steps/Address';
import Approved from './steps/Approved';
import ContactInfo from './steps/ContactInfo';
import Dob from './steps/Dob';
import EditContactInfo from './steps/EditContactInfo';
import { EmploymentStep } from './steps/Employment';
import Explainer from './steps/Explainer';
import Review from './steps/Review';
import Ssn from './steps/Ssn';
import UnderReview from './steps/UnderReview';

enum KycStep {
    EXPLAINER = 'explainer',
    CONTACT_INFO = 'contactInfo',
    EDIT_CONTACT_INFO = 'editContactInfo',
    SSN = 'ssn',
    DOB = 'dob',
    ADDRESS = 'address',
    EMPLOYMENT = 'employment',
    REVIEW = 'review',
    APPROVED = 'approved',
    UNDER_REVIEW = 'underReview',
}

// Defined in backend - must stay in sync
export enum KycFlowSourceEnum {
    SINGLE_CASH_ACCOUNT_FLOW = 'single_cash_account_flow',
    GO_FLOW_ACCOUNT_OPENING = 'go_flow_account_opening',
}

const Kyc = ({
    kycFlowSource,
    isInsideModal = false,
    isOpen = false,
    onClose,
}: {
    kycFlowSource: KycFlowSourceEnum;
    isInsideModal?: boolean;
    isOpen?: boolean;
    onClose?: (isKycAccepted: boolean) => void;
}) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('KYC');
    const { jointInviteBankAccountId } = useLocalSearchParams<{
        jointInviteBankAccountId?: string;
    }>();

    const toast = useToastController();
    const queryClient = useQueryClient();

    const user = useAuthedUser();
    const { refetch: refetchUser } = useUser();

    const routes: Array<{ key: KycStep }> = Object.values(KycStep).map(
        (step) => ({ key: step }),
    );

    const getRouteIndex = (kycStep: KycStep) =>
        routes.findIndex(({ key }) => key === kycStep);

    const contactInfoIndex = getRouteIndex(KycStep.CONTACT_INFO);
    const editContactInfoIndex = getRouteIndex(KycStep.EDIT_CONTACT_INFO);
    const ssnIndex = getRouteIndex(KycStep.SSN);
    const dobIndex = getRouteIndex(KycStep.DOB);
    const addressIndex = getRouteIndex(KycStep.ADDRESS);
    const employmentIndex = getRouteIndex(KycStep.EMPLOYMENT);
    const reviewIndex = getRouteIndex(KycStep.REVIEW);
    const approvedIndex = getRouteIndex(KycStep.APPROVED);
    const underReviewIndex = getRouteIndex(KycStep.UNDER_REVIEW);

    // Use these values to display dot navigation for just a subset of KYC pages
    const dotNavigationIndices = [
        contactInfoIndex,
        ssnIndex,
        dobIndex,
        addressIndex,
        employmentIndex,
        reviewIndex,
    ];

    const [index, setIndex] = useState(0);

    const hasCloseButton =
        [approvedIndex, underReviewIndex].includes(index) ||
        (index === 0 && isInsideModal);

    const [kycData, setKycData] = useState<Partial<RunKYCVerifyBody>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const setAddress = useSetAtom(addressAtom);

    const { mutateAsync: runKYCVerify } = useRunKYCVerify();

    const onSubmit = async () => {
        setIsSubmitting(true);

        try {
            const verifiedUser = await runKYCVerify({
                body: {
                    ...(kycData as RunKYCVerifyBody),
                    // Email and phone number are not editable in the UX, just pass along to send with KYC request
                    email: user.email,
                    phoneNumber: user.phoneNumber as string,
                    disclosures: getBankingDisclosures({
                        withKyc: true,
                        // No disclosures are shown on Review.tsx when filling out KYC from the go flow, but we do want to acknowledge KYC_DATA_COLLECTION
                        policyTypes: isInsideModal
                            ? []
                            : FRUITFUL_CASH_ACCOUNT_OPEN_POLICY_DISCLOSURES,
                    }),
                    kycFlowSource,
                },
                urlParams: {
                    userId: user.id,
                },
            });

            // Wrap this in a timeout to wait for the webhook to settle on the backend
            setTimeout(async () => {
                // Invalidate the query cache for the user because KYC status and joint onboarding status has now changed
                await queryClient.invalidateQueries({
                    queryKey: [`getCustomer/${user.stytchId}`],
                });
                await refetchUser?.(); // Await this refetch because logic following this might depend on having the kyc result on user

                if (
                    verifiedUser.syncteraKycStatus ===
                    KYCVerificationStatus.ACCEPTED
                ) {
                    setIsSubmitting(false);

                    const {
                        firstName,
                        lastName,
                        phoneNumber,
                        line1,
                        line2,
                        city,
                        postalCode,
                        state,
                    } = kycData as PrepopulatedAddress;

                    setAddress({
                        firstName,
                        lastName,
                        phoneNumber,
                        line1,
                        ...(line2 != null && { line2 }),
                        city,
                        postalCode,
                        ...(state != null && { state }),
                    });

                    if (isInsideModal) {
                        onClose?.(true);
                    } else {
                        router.navigate({
                            pathname: '/banking/onboarding/space-ownership',
                            params: {
                                fromKyc: 'true',
                                // jointInviteBankAccountId param will be used to skip account ownership selection
                                ...(jointInviteBankAccountId != null
                                    ? { jointInviteBankAccountId }
                                    : {}),
                            },
                        });
                    }
                } else {
                    setIsSubmitting(false);
                    setIndex(underReviewIndex);
                }
            }, 3000);
        } catch (e) {
            captureException(e);
            setIsSubmitting(false);
            toast.show('Something went wrong');
        }
    };

    const updateKycData = (formData?: Partial<RunKYCVerifyBody>) => {
        formData != null &&
            setKycData({
                ...kycData,
                ...formData,
            });
    };

    const onPressNext = (formData?: Partial<RunKYCVerifyBody>) => {
        updateKycData(formData);
        if (index === contactInfoIndex) {
            // Skip edit contact info page
            setIndex(index + 2);
        } else {
            setIndex(index + 1);
        }
    };

    const onPressBack = () => {
        if (hasCloseButton) {
            if (isInsideModal) {
                onClose?.(false);
                return;
            } else {
                router.replace('/home/banking');
            }
        }

        if (index - 1 === editContactInfoIndex) {
            // Skip over edit page in navigation
            setIndex(index - 2);
        } else if (index > 0) {
            setIndex(index - 1);
        } else {
            if (router.canGoBack()) {
                router.back();
            } else {
                router.replace('/home/banking');
            }
        }
    };

    const renderDotNavigation = () => {
        if (!dotNavigationIndices.includes(index)) {
            return null;
        }
        return (
            <View
                marginBottom={8}
                marginTop={isInsideModal ? -4 : isNonMobileWeb ? 0 : -16}
            >
                <DotNavigation
                    total={dotNavigationIndices.length}
                    currentIndex={dotNavigationIndices.findIndex(
                        (i) => i === index,
                    )}
                />
            </View>
        );
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: KycStep } }) => {
        // Only render the current scene
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const getStepContent = () => {
            switch (route.key) {
                case KycStep.EXPLAINER:
                    return <Explainer onPressNext={onPressNext} />;
                case KycStep.CONTACT_INFO:
                    return (
                        <ContactInfo
                            user={user}
                            kycData={kycData}
                            onPressNext={onPressNext}
                            onPressEdit={() => {
                                setIndex(editContactInfoIndex);
                            }}
                        />
                    );
                case KycStep.EDIT_CONTACT_INFO:
                    return (
                        <View flex={1} marginTop={isInsideModal ? 32 : 8}>
                            <EditContactInfo
                                user={user}
                                onPressNext={onPressNext}
                            />
                        </View>
                    );
                case KycStep.SSN:
                    return (
                        <Ssn
                            onPressNext={onPressNext}
                            saveValueForNextFlow={isInsideModal}
                        />
                    );
                case KycStep.DOB:
                    return (
                        <Dob
                            dateOfBirth={
                                kycData.dateOfBirth ?? user.dateOfBirth
                            }
                            onPressNext={onPressNext}
                        />
                    );
                case KycStep.ADDRESS:
                    return (
                        <Address
                            kycData={kycData}
                            onPressNext={onPressNext}
                            user={user}
                        />
                    );
                case KycStep.EMPLOYMENT:
                    return (
                        <EmploymentStep
                            kycData={kycData}
                            setKycData={updateKycData}
                            onPressNext={onPressNext}
                        />
                    );
                case KycStep.REVIEW:
                    return (
                        <Review
                            isInsideModal={isInsideModal}
                            kycData={kycData as RunKYCVerifyBody}
                            onPressSubmit={onSubmit}
                        />
                    );
                case KycStep.APPROVED:
                    return <Approved />;
                case KycStep.UNDER_REVIEW:
                    return <UnderReview />;
                default:
                    return <></>;
            }
        };

        trackViewOnce(`KYC_${route.key.toUpperCase()}`);

        return (
            <View flex={1} paddingHorizontal={2}>
                {getStepContent()}
            </View>
        );
    };

    if (isSubmitting) return <FullScreenSpinner />;

    if (isInsideModal) {
        return (
            <TallSheet
                isOpen={isOpen}
                onClose={onPressBack}
                disableDrag={true}
                dismissOnOverlayPress={false}
                showCloseButton={hasCloseButton}
                headerLeftAction={
                    hasCloseButton ? undefined : (
                        <BackButton
                            customBackFn={onPressBack}
                            iconType={BackButtonIconType.ARROW_LEFT}
                        />
                    )
                }
            >
                <View
                    flex={1}
                    $sm={{
                        height: 600,
                        maxHeight: '80vh',
                    }}
                >
                    {renderDotNavigation()}
                    <TabView
                        swipeEnabled={false}
                        navigationState={{ index, routes }}
                        renderScene={renderScene}
                        renderTabBar={() => null}
                        onIndexChange={setIndex}
                    />
                </View>
            </TallSheet>
        );
    }

    return (
        <ScreenContainer
            hasBackButton={!hasCloseButton}
            hasCloseButton={hasCloseButton}
            onBack={onPressBack}
            onClose={onPressBack}
            onFallbackRoute="/home/banking"
        >
            <View height="100%">
                {renderDotNavigation()}
                <TabView
                    swipeEnabled={false}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    renderTabBar={() => null}
                    onIndexChange={setIndex}
                />
            </View>
        </ScreenContainer>
    );
};

export default Kyc;
